// Local stats and streaks — the fallback for when the hub is unconfigured.
//
// "The game must be fully playable with no HUB_* env set" is a non-negotiable, and a daily puzzle
// with no sense of continuity is a worse game. This is the local half: pure functions over a plain
// record, so the storage adapter (localStorage, in the UI layer) has no logic in it and this can be
// tested without a browser.
//
// The hub owns the real streak once configured. This never syncs, never wins a conflict, and is
// deliberately not authoritative — it is what the player sees when there is nobody to ask.

/** One finished daily. Keyed by the Sydney date string, so replaying a day cannot inflate a streak. */
export interface StatsEntry {
  key: string
  score: number
  durationMs: number
  won: boolean
}

export interface Stats {
  /** Most recent first. */
  history: StatsEntry[]
  played: number
  won: number
  currentStreak: number
  bestStreak: number
  /** Best score achieved, across all days. */
  bestScore: number
}

export const EMPTY_STATS: Stats = {
  history: [],
  played: 0,
  won: 0,
  currentStreak: 0,
  bestStreak: 0,
  bestScore: 0,
}

/** How many days of history to keep. Enough for an archive view later without unbounded growth. */
export const HISTORY_LIMIT = 365

/** Days between two YYYY-MM-DD keys. Both are Sydney calendar dates, so this is plain UTC maths. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  return Math.round((b - a) / 86_400_000)
}

/**
 * Fold one finished run into the stats.
 *
 * Rules that matter:
 *   * **A day counts once.** Re-submitting the same key updates that day's entry rather than adding
 *     to it — otherwise replaying today inflates every counter, and the local streak becomes a
 *     number you can farm by pressing a button.
 *   * **A streak needs consecutive calendar days.** A gap of 2+ days restarts it. Playing yesterday
 *     and today continues it. Playing today twice does neither.
 *   * **Only a win extends a streak.** An unfinished run is played, not won, and breaks it — the
 *     streak is a record of penning the flock, not of opening the app.
 */
export function recordRun(prev: Stats, entry: StatsEntry): Stats {
  const existing = prev.history.find((h) => h.key === entry.key)
  if (existing) {
    // Same day again: keep the better score, but do not touch any counter.
    const history = prev.history.map((h) =>
      h.key === entry.key
        ? { ...h, score: Math.max(h.score, entry.score), won: h.won || entry.won }
        : h,
    )
    return { ...prev, history, bestScore: Math.max(prev.bestScore, entry.score) }
  }

  const previousDay = prev.history[0]
  const consecutive = previousDay ? daysBetween(previousDay.key, entry.key) === 1 : false

  let currentStreak: number
  if (!entry.won) currentStreak = 0
  else if (consecutive && previousDay?.won) currentStreak = prev.currentStreak + 1
  else currentStreak = 1

  const history = [entry, ...prev.history].slice(0, HISTORY_LIMIT)

  return {
    history,
    played: prev.played + 1,
    won: prev.won + (entry.won ? 1 : 0),
    currentStreak,
    bestStreak: Math.max(prev.bestStreak, currentStreak),
    bestScore: Math.max(prev.bestScore, entry.score),
  }
}

/**
 * The streak as it should be DISPLAYED, which is not always the stored one.
 *
 * A stored streak goes stale the moment a day is missed, and nothing runs while the app is closed
 * to notice. Someone with a 9-day streak who skips two days must not open the app and still see 9.
 */
export function displayStreak(stats: Stats, todayKey: string): number {
  const last = stats.history[0]
  if (!last || !last.won) return 0
  const gap = daysBetween(last.key, todayKey)
  // 0 = played today, 1 = played yesterday and today is still live.
  return gap <= 1 ? stats.currentStreak : 0
}
